// COPIA DO MODO EMERGENCIA (lib/emergencia.ts). Enquanto o sistema esta no ar, guarda no navegador
// (IndexedDB) o que as quatro telas da emergencia precisam: produtos, clientes e as ultimas respostas
// das listas pequenas (formas de pagamento, grupos, marcas, categorias, lista de orcamentos). Com o
// sistema fora, responde as chamadas dessas telas a partir da copia, e grava no aparelho os
// orcamentos e clientes feitos na queda — que sobem quando o servidor volta e o usuario clica "Voltar ao normal" ([DA-849])
// (enviarPendentes, no fim do arquivo).
//
// Decisoes do Ruan (24/10/2026): cliente da emergencia que ja existe (CPF ou WhatsApp) usa o
// existente; a lista de orcamentos mostra os da emergencia + a ultima copia vista antes da queda;
// PDF desabilitado (o numero EMG-... nao e' o ORC que o orcamento vai ganhar).
import { api, ApiError, getToken, registrarObservadorResposta, registrarRespostaEmergencia, type ApiOptions } from './api';
import { emEmergencia, estaSemConexao, lerUsuarioGuardado } from './emergencia';

const INTERVALO_COPIA_MS = 30 * 60_000;
const ATRASO_PRIMEIRA_COPIA_MS = 20_000;

// ---------------- IndexedDB (chave -> valor) ----------------
const BANCO = 'fiodecobre-emergencia';
const LOJA = 'kv';
let bancoP: Promise<IDBDatabase> | null = null;
function banco(): Promise<IDBDatabase> {
  if (!bancoP) {
    bancoP = new Promise((resolve, reject) => {
      const r = indexedDB.open(BANCO, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(LOJA);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  }
  return bancoP;
}
async function ler<T>(chave: string): Promise<T | undefined> {
  const db = await banco();
  return new Promise((resolve, reject) => {
    const r = db.transaction(LOJA, 'readonly').objectStore(LOJA).get(chave);
    r.onsuccess = () => resolve(r.result as T | undefined);
    r.onerror = () => reject(r.error);
  });
}
async function gravar(chave: string, valor: unknown): Promise<void> {
  const db = await banco();
  return new Promise((resolve, reject) => {
    const t = db.transaction(LOJA, 'readwrite');
    t.objectStore(LOJA).put(valor, chave);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

// ---------------- estado em memoria ----------------
type Linha = Record<string, any>;
interface Copia { produtos: Linha[]; clientes: Linha[]; atualizado_em: string }
export interface ClienteLocal extends Linha { id: number; nome: string; emergencia: true; id_real?: number | null }
export interface OrcamentoLocal {
  id: number;            // negativo: nunca colide com o banco
  chave: string;         // uuid: o servidor nao cria duas vezes o mesmo (orcamentos.emergencia_chave)
  numero: string;        // EMG-...
  criado_em: string;
  vendedor_id: number | null;
  vendedor_login: string | null;
  payload: Linha;        // o corpo que o OrcamentoNovo mandou no ultimo POST/PUT
  enviado_numero?: string | null;  // ORC-... depois que subiu
  enviado_id?: number | null;
  erro_envio?: string | null;
}
interface Locais { clientes: ClienteLocal[]; orcamentos: OrcamentoLocal[]; seq: number }

let copia: Copia | null = null;
let respostas: Record<string, { dados: unknown; em: string }> = {};
let locais: Locais = { clientes: [], orcamentos: [], seq: 0 };
const carregado = (async () => {
  try {
    copia = (await ler<Copia>('copia')) ?? null;
    respostas = (await ler<typeof respostas>('respostas')) ?? {};
    locais = (await ler<Locais>('locais')) ?? locais;
  } catch (e) {
    console.error('[emergencia] nao consegui ler a copia guardada:', e);
  }
})();

export function copiaAtualizadaEm(): string | null {
  return copia?.atualizado_em ?? null;
}
export function orcamentosLocais(): OrcamentoLocal[] {
  return locais.orcamentos;
}
async function salvarLocais(): Promise<void> {
  await gravar('locais', locais);
}

// ---------------- respostas guardadas ao passar ----------------
// GETs pequenos que as telas da emergencia fazem ao abrir: a ultima resposta de cada URL fica.
const GUARDAR_AO_PASSAR = [
  '/api/orcamentos/aux/formas-pagamento',
  '/api/grupos',
  '/api/marcas',
  '/api/categorias',
  '/api/categorias/sem-vinculo',
  '/api/orcamentos',
];
let gravacaoAgendada: ReturnType<typeof setTimeout> | null = null;
registrarObservadorResposta((url, dados) => {
  if (!GUARDAR_AO_PASSAR.includes(url.pathname)) return;
  respostas[url.pathname + url.search] = { dados, em: new Date().toISOString() };
  // A lista de orcamentos tambem guarda a mais recente de QUALQUER filtro: offline, a aba que a
  // pessoa abrir pode nao ter sido vista exatamente assim.
  if (url.pathname === '/api/orcamentos') respostas['/api/orcamentos#ultima'] = { dados, em: new Date().toISOString() };
  if (gravacaoAgendada) clearTimeout(gravacaoAgendada);
  gravacaoAgendada = setTimeout(() => { void gravar('respostas', respostas).catch(() => {}); }, 2000);
});

// ---------------- atualizar a copia (sistema no ar) ----------------
export async function atualizarCopia(): Promise<void> {
  if (estaSemConexao() || emEmergencia() || !getToken()) return;
  // A lista INTEIRA de produtos ativos no formato da propria rota (ordem_descricao tira o teto de
  // 500; incluir_a_caminho porque a venda ve os "a caminho").
  const produtos = await api.get<Linha[]>('/api/produtos', { query: { ordem_descricao: 'asc', incluir_a_caminho: '1' } });
  const clientes = await api.get<Linha[]>('/api/emergencia/clientes');
  copia = { produtos, clientes, atualizado_em: new Date().toISOString() };
  await gravar('copia', copia);
}

let relogioCopia: ReturnType<typeof setTimeout> | null = null;
export function iniciarCopia(): void {
  const rodar = async () => {
    try { await atualizarCopia(); } catch (e) { console.error('[emergencia] copia nao atualizou:', e); }
    relogioCopia = setTimeout(rodar, INTERVALO_COPIA_MS);
  };
  if (relogioCopia) clearTimeout(relogioCopia);
  relogioCopia = setTimeout(rodar, ATRASO_PRIMEIRA_COPIA_MS);
}

// ---------------- busca offline ----------------
const semAcento = (s: unknown) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
const num = (v: unknown) => (v == null || v === '' ? 0 : Number(v));

// Mesmo jeito da rota (produtos.routes.ts, DA-389/DA-501): todas as palavras em algum campo;
// codigo exato e "comeca com" na frente, alfabetico desempata.
function buscarProdutos(q: URLSearchParams): Linha[] {
  let lista = (copia?.produtos ?? []).filter((p) => p.status === 'ativo' || !p.status);
  const g = q.get('grupo_id'); if (g) lista = lista.filter((p) => String(p.grupo_id) === g);
  const c = q.get('categoria_id');
  if (c) lista = lista.filter((p) => String(p.categoria_id) === c);
  else if (q.get('sem_categoria') === '1') lista = lista.filter((p) => p.categoria_id == null);
  const m = q.get('marca_id'); if (m) lista = lista.filter((p) => String(p.marca_id) === m);
  if (q.get('catalogo') === 'true') lista = lista.filter((p) => p.postar_catalogo);
  if (q.get('somente_vendaveis') === 'true') lista = lista.filter((p) => !p.uso_consumo);
  if (q.get('sem_estoque') === '1') lista = lista.filter((p) => num(p.estoque_efetivo ?? p.estoque_atual) <= 0);
  if (q.get('esconder_a_caminho') === '1' && q.get('incluir_a_caminho') !== '1') lista = lista.filter((p) => !p.compra_em_aberto);
  const ids = (q.get('ids') ?? '').split(',').filter(Boolean);
  if (ids.length) lista = lista.filter((p) => ids.includes(String(p.id)));

  const termo = semAcento(q.get('q')).trim();
  if (termo) {
    const palavras = termo.split(/\s+/);
    const numerico = /^\d+$/.test(termo);
    const nivel = (p: Linha) => {
      const desc = semAcento(p.descricao);
      if (numerico) {
        if (p.codigo_interno === termo.padStart(4, '0')) return 0;
        if ([p.codigo_interno, p.ean, p.referencia].some((x) => String(x ?? '').startsWith(termo))) return 1;
        return 2;
      }
      if (desc.startsWith(termo)) return 0;
      if (desc.includes(termo)) return 1;
      if (palavras.every((w) => desc.includes(w))) return 2;
      return 3;
    };
    lista = lista
      .filter((p) => {
        const tudo = semAcento([p.codigo_interno, p.ean, p.referencia, p.descricao, p.outras_descricoes].join(' '));
        return palavras.every((w) => tudo.includes(w));
      })
      .map((p) => ({ p, n: nivel(p) }))
      .sort((a, b) => a.n - b.n || String(a.p.descricao).localeCompare(String(b.p.descricao), 'pt-BR'))
      .map((x) => x.p);
  }
  const est = q.get('ordem_estoque');
  if (est) lista = [...lista].sort((a, b) => (num(a.estoque_atual) - num(b.estoque_atual)) * (est === 'desc' ? -1 : 1));
  const inteira = est || q.get('ordem_minimo') || q.get('ordem_descricao') || q.get('sem_estoque') === '1';
  return inteira ? lista : lista.slice(0, 500);
}

function todosClientes(): Linha[] {
  return [...locais.clientes, ...(copia?.clientes ?? [])];
}
function buscarClientes(q: URLSearchParams): Linha[] {
  let lista = todosClientes().filter((c) => c.status !== 'inativo' || q.get('incluir_inativos') === 'true');
  if (q.get('programa_prestador') === 'true') lista = lista.filter((c) => c.programa_prestador);
  const termo = semAcento(q.get('busca')).replace(/Ç/g, 'C').trim();
  if (termo) {
    lista = lista.filter((c) =>
      [c.nome, c.razao_social, c.codigo_legado, c.cpf_cnpj, c.telefone, c.whatsapp].some((x) => semAcento(x).includes(termo)));
  }
  return lista.sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR')).slice(0, 500);
}

const soDigitos = (s: unknown) => String(s ?? '').replace(/\D/g, '');
// Ultimos 8 digitos: casa as grafias com e sem o 9o digito e com/sem 55 (a chave do servidor e'
// a `chave_telefone_br`; aqui e' so pra achar o cliente que JA existe sem duplicar na queda).
const chaveTel = (s: unknown) => soDigitos(s).slice(-8);
function clienteExistente(corpo: Linha): Linha | undefined {
  const doc = soDigitos(corpo.cpf_cnpj);
  const tels = [corpo.whatsapp, corpo.telefone, ...((corpo.telefones ?? []) as Linha[]).map((t) => t.numero)]
    .map(chaveTel).filter((t) => t.length === 8);
  return todosClientes().find((c) =>
    (doc && soDigitos(c.cpf_cnpj) === doc) ||
    (tels.length && [c.whatsapp, c.telefone].map(chaveTel).some((t) => t.length === 8 && tels.includes(t))));
}

function produtoDaCopia(id: number): Linha | undefined {
  return copia?.produtos.find((p) => p.id === id);
}

// O formato do GET /api/orcamentos/:id que o OrcamentoNovo le pra reabrir o orcamento.
function orcamentoCarregado(o: OrcamentoLocal): Linha {
  const p = o.payload;
  const cli = todosClientes().find((c) => c.id === p.cliente_id);
  return {
    numero: o.numero,
    vendedor_login: o.vendedor_login,
    criado_em: o.criado_em,
    status: 'emergencia',
    tipo: p.tipo ?? 'venda',
    cliente_id: p.cliente_id ?? 1,
    cliente_nome: cli?.nome ?? p.cliente_nome_custom ?? 'Consumidor',
    cliente_codigo: cli?.codigo_legado ?? null,
    telefone_recusado: !!p.telefone_recusado,
    contato_capturado_id: p.contato_capturado_id ?? null,
    prestador_id: p.prestador_id ?? null,
    prestador_nome: todosClientes().find((c) => c.id === p.prestador_id)?.nome ?? null,
    tipo_nf: p.tipo_nf ?? 'nfce',
    forma_pagamento_id: p.forma_pagamento_id ?? null,
    observacoes: p.observacoes ?? '',
    comprador: p.comprador ?? null,
    senha_gerencial_usada: !!p.senha_gerencial_usada,
    override_base: p.override_base ?? null,
    override_base_auto: p.override_base_auto ?? null,
    itens: ((p.itens ?? []) as Linha[]).map((it) => {
      const pr = produtoDaCopia(it.produto_id) ?? {};
      return {
        ...it,
        codigo_interno: pr.codigo_interno ?? null,
        produto_descricao: pr.descricao ?? `PRODUTO ${it.produto_id}`,
        foto_url: pr.foto_url ?? null,
        unidade_sigla: pr.unidade_sigla ?? null,
        marca_nome: pr.marca_nome ?? null,
        permite_fracionado: !!pr.permite_fracionado,
        estoque_atual: pr.estoque_atual ?? null,
        estoque_efetivo: pr.estoque_efetivo ?? null,
        localizacao: pr.localizacao ?? null,
        preco_cartao_catalogo: pr.preco_cartao ?? null,
        preco_pix: pr.preco_pix ?? null,
      };
    }),
  };
}

// A linha da lista de orcamentos (OrcamentosHome) pra um orcamento da emergencia.
function linhaDaLista(o: OrcamentoLocal): Linha {
  const p = o.payload;
  const cli = todosClientes().find((c) => c.id === p.cliente_id);
  const itens = (p.itens ?? []) as Linha[];
  const total = itens.reduce((s, it) => s + Math.round(num(it.subtotal) * 100), 0) / 100;
  return {
    id: o.id,
    numero: o.enviado_numero ?? o.numero,
    cliente_id: p.cliente_id ?? null,
    cliente_nome: cli?.nome ?? 'Consumidor',
    cliente_nome_custom: p.cliente_nome_custom ?? null,
    vendedor_nome: o.vendedor_login,
    vendedor_login: o.vendedor_login,
    status: 'emergencia',
    total: total.toFixed(2),
    total_itens: itens.length,
    retornado: false,
    retornado_por: null,
    retornado_em_fmt: null,
    criado_em: o.criado_em,
    tipo_documento: null,
    tipo: p.tipo ?? 'venda',
  };
}

function idDoCaminho(caminho: string, prefixo: string): number | null {
  const m = caminho.match(new RegExp('^' + prefixo + '/(-?\\d+)(/.*)?$'));
  return m ? Number(m[1]) : null;
}

// ---------------- respostas offline ----------------
registrarRespostaEmergencia((method: string, url: URL, opts: ApiOptions) => {
  const caminho = url.pathname;
  const q = url.searchParams;

  if (method === 'GET') {
    if (caminho === '/api/produtos') return carregado.then(() => buscarProdutos(q));
    if (caminho === '/api/clientes') return carregado.then(() => buscarClientes(q));
    if (caminho === '/api/clientes/contatos-sem-cadastro') return Promise.resolve([]);
    const cid = idDoCaminho(caminho, '/api/clientes');
    if (cid != null && caminho === `/api/clientes/${cid}`) {
      return carregado.then(() => {
        const c = todosClientes().find((x) => x.id === cid);
        if (!c) throw new ApiError(404, 'Cliente nao esta na copia deste computador');
        return c;
      });
    }
    if (caminho === '/api/orcamentos') {
      return carregado.then(() => {
        const guardada = respostas[caminho + url.search] ?? respostas['/api/orcamentos#ultima'];
        let antes = ((guardada?.dados as Linha[]) ?? []);
        const status = q.get('status');
        if (status && !respostas[caminho + url.search]) antes = antes.filter((o) => o.status === status);
        const daQueda = locais.orcamentos.filter((o) => !o.enviado_id).map(linhaDaLista);
        return [...daQueda, ...antes];
      });
    }
    const oid = idDoCaminho(caminho, '/api/orcamentos');
    if (oid != null && oid < 0) {
      return carregado.then(() => {
        const o = locais.orcamentos.find((x) => x.id === oid);
        if (!o) throw new ApiError(404, 'Orcamento da emergencia nao encontrado neste computador');
        if (caminho.endsWith('/itens-resumo')) {
          return orcamentoCarregado(o).itens.map((it: Linha) => ({
            codigo: it.codigo_interno, descricao: it.produto_descricao, foto_url: it.foto_url,
            quantidade: String(it.quantidade), preco_unitario: String(it.preco_unitario),
            subtotal: String(it.subtotal), unidade: it.unidade_sigla ?? '',
          }));
        }
        return orcamentoCarregado(o);
      });
    }
    const guardada = respostas[caminho + url.search] ?? respostas[caminho];
    if (guardada && GUARDAR_AO_PASSAR.includes(caminho)) return Promise.resolve(guardada.dados);
    return undefined;
  }

  // Trava de edicao (DA-406) do orcamento da emergencia: so este aparelho o conhece.
  const oid = idDoCaminho(caminho, '/api/orcamentos');
  if (oid != null && oid < 0 && caminho.endsWith('/editando')) return Promise.resolve({ ok: true });

  if (method === 'POST' && caminho === '/api/orcamentos') {
    return carregado.then(async () => {
      const u = lerUsuarioGuardado<{ id: number; login: string }>();
      locais.seq += 1;
      const agora = new Date();
      const o: OrcamentoLocal = {
        id: -(Date.now() % 1_000_000_000) - locais.seq,
        chave: crypto.randomUUID(),
        numero: `EMG-${String(agora.getDate()).padStart(2, '0')}${String(agora.getMonth() + 1).padStart(2, '0')}-${locais.seq}`,
        criado_em: agora.toISOString(),
        vendedor_id: u?.id ?? null,
        vendedor_login: u?.login ?? null,
        payload: (opts.body ?? {}) as Linha,
      };
      locais.orcamentos.push(o);
      await salvarLocais();
      return { id: o.id, numero: o.numero };
    });
  }
  if (method === 'PUT' && oid != null && oid < 0 && caminho === `/api/orcamentos/${oid}`) {
    return carregado.then(async () => {
      const o = locais.orcamentos.find((x) => x.id === oid);
      if (!o) throw new ApiError(404, 'Orcamento da emergencia nao encontrado neste computador');
      o.payload = (opts.body ?? {}) as Linha;
      await salvarLocais();
      return { ok: true };
    });
  }

  if (method === 'POST' && caminho === '/api/clientes') {
    return carregado.then(async () => {
      const corpo = (opts.body ?? {}) as Linha;
      const nome = semAcento(corpo.nome).trim();
      if (!nome) throw new ApiError(400, 'nome: obrigatorio');
      // Decisao do Ruan: quem ja existe (CPF ou WhatsApp) e' usado, nunca duplicado.
      const ja = clienteExistente(corpo);
      if (ja) return ja;
      locais.seq += 1;
      const c: ClienteLocal = {
        ...corpo,
        id: -(Date.now() % 1_000_000_000) - locais.seq,
        nome,
        status: 'ativo',
        codigo_legado: null,
        emergencia: true,
      };
      locais.clientes.push(c);
      await salvarLocais();
      return c;
    });
  }
  return undefined;
});

// ---------------- envio dos pendentes (servidor voltou) ----------------

/**
 * O cliente cadastrado na queda sobe pelo POST /api/clientes DE SEMPRE — as mesmas regras de nome,
 * telefone e duplicidade do cadastro normal. Ja existe (409, mesmo WhatsApp ou CPF)? Usa o existente
 * (decisao do Ruan). O id de verdade fica guardado: um segundo orcamento do mesmo cliente nao o
 * cria de novo.
 */
async function clienteDeVerdade(idLocal: number): Promise<number> {
  const c = locais.clientes.find((x) => x.id === idLocal);
  if (!c) return 1;
  if (c.id_real) return c.id_real;
  const { id: _id, emergencia: _e, id_real: _r, codigo_legado: _cl, status: _s, ...corpo } = c;
  let idReal: number | null = null;
  try {
    const criado = await api.post<{ id: number }>('/api/clientes', corpo);
    idReal = criado.id;
  } catch (e) {
    if (!(e instanceof ApiError)) throw e;
    const existente = (e.corpo as { cliente_existente?: { id: number } } | undefined)?.cliente_existente;
    if (e.status === 409 && existente?.id) {
      idReal = existente.id;
    } else if (e.status === 409 && c.cpf_cnpj) {
      const achados = await api.get<Linha[]>('/api/clientes', { query: { busca: soDigitos(c.cpf_cnpj), incluir_inativos: 'true' } });
      idReal = achados.find((x) => soDigitos(x.cpf_cnpj) === soDigitos(c.cpf_cnpj))?.id ?? null;
    } else if (e.status === 400 && (e.corpo as { codigo?: string } | undefined)?.codigo === 'sem_whatsapp') {
      // O numero digitado na queda nao tem WhatsApp: cadastra sem ele, com o numero no telefone.
      const criado = await api.post<{ id: number }>('/api/clientes', { ...corpo, whatsapp: null, telefones: undefined, telefone: corpo.whatsapp ?? corpo.telefone ?? null });
      idReal = criado.id;
    } else {
      throw e;
    }
  }
  if (!idReal) throw new Error(`Cliente ${c.nome} nao foi encontrado nem criado`);
  c.id_real = idReal;
  await salvarLocais();
  return idReal;
}

let enviando = false;
/** Sobe ao servidor os orcamentos da emergencia ainda nao enviados. Idempotente no servidor (pelo numero EMG). */
export async function enviarPendentes(): Promise<{ enviados: number; falhas: number }> {
  await carregado;
  if (enviando || estaSemConexao() || emEmergencia() || !getToken()) return { enviados: 0, falhas: 0 };
  enviando = true;
  let enviados = 0;
  let falhas = 0;
  try {
    for (const o of locais.orcamentos) {
      if (o.enviado_id) continue;
      try {
        const clienteId = Number(o.payload.cliente_id) < 0 ? await clienteDeVerdade(Number(o.payload.cliente_id)) : o.payload.cliente_id;
        const r = await api.post<{ id: number; numero: string }>('/api/emergencia/orcamentos', {
          emergencia_chave: o.chave,
          emergencia_numero: o.numero,
          criado_em: o.criado_em,
          vendedor_id: o.vendedor_id,
          orcamento: { ...o.payload, cliente_id: clienteId },
        });
        o.enviado_id = r.id;
        o.enviado_numero = r.numero;
        o.erro_envio = null;
        enviados++;
      } catch (e) {
        o.erro_envio = e instanceof Error ? e.message : String(e);
        falhas++;
        console.error('[emergencia] orcamento', o.numero, 'nao subiu:', e);
      }
      await salvarLocais();
    }
  } finally {
    enviando = false;
  }
  return { enviados, falhas };
}
